
import React from 'react';
import { Activity, Zap, TrendingDown, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

interface HvacEnergyStatsProps {
  selectedFloor: number;
  selectedZone: string | null;
}

// Mock data for energy consumption per zone
const energyData = [
  { 
    floor: 1,
    zones: [
      { id: 'gf-zone1', name: 'Reception', consumption: 42, trend: 'down', percentage: 5 },
      { id: 'gf-zone2', name: 'Lobby', consumption: 35, trend: 'up', percentage: 3 },
      { id: 'gf-zone3', name: 'Meeting Room A', consumption: 28, trend: 'down', percentage: 7 },
      { id: 'gf-zone4', name: 'Cafeteria', consumption: 56, trend: 'up', percentage: 8 },
    ] 
  },
  { 
    floor: 2,
    zones: [
      { id: 'ff-zone1', name: 'Open Office', consumption: 68, trend: 'down', percentage: 4 },
      { id: 'ff-zone2', name: 'Meeting Room B', consumption: 22, trend: 'down', percentage: 6 },
      { id: 'ff-zone3', name: 'Executive Office', consumption: 45, trend: 'up', percentage: 9 },
      { id: 'ff-zone4', name: 'IT Department', consumption: 75, trend: 'up', percentage: 12 },
    ] 
  },
  { 
    floor: 3,
    zones: [
      { id: 'sf-zone1', name: 'Research Lab', consumption: 58, trend: 'down', percentage: 3 },
      { id: 'sf-zone2', name: 'Development', consumption: 62, trend: 'up', percentage: 5 },
      { id: 'sf-zone3', name: 'Conference Room', consumption: 32, trend: 'down', percentage: 8 },
      { id: 'sf-zone4', name: 'Break Room', consumption: 28, trend: 'down', percentage: 4 },
    ] 
  }
];

// Mock data for hourly consumption
const hourlyData = [
  { hour: '6am', consumption: 20 },
  { hour: '8am', consumption: 45 },
  { hour: '10am', consumption: 68 },
  { hour: '12pm', consumption: 72 },
  { hour: '2pm', consumption: 78 },
  { hour: '4pm', consumption: 65 },
  { hour: '6pm', consumption: 40 },
];

const HvacEnergyStats: React.FC<HvacEnergyStatsProps> = ({ selectedFloor, selectedZone }) => {
  // Get current floor data
  const currentFloorData = energyData.find(data => data.floor === selectedFloor) || energyData[0];
  
  // Get selected zone data if available
  const selectedZoneData = selectedZone 
    ? currentFloorData.zones.find(zone => zone.id === selectedZone) 
    : null;
  
  // Display data for either the selected zone or the whole floor
  const displayData = selectedZoneData 
    ? [selectedZoneData] 
    : currentFloorData.zones;

  return (
    <div className="p-4 h-full">
      <h2 className="text-lg font-semibold mb-3 flex items-center">
        <Zap className="mr-2 h-5 w-5" />
        Energy Consumption Stats
      </h2>
      
      {selectedZoneData ? (
        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md mb-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="font-medium">{selectedZoneData.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Selected Zone</p>
            </div>
            <div className="flex items-center">
              <span className="text-lg font-bold text-gray-800 dark:text-gray-200">
                {selectedZoneData.consumption} kWh
              </span>
              <span className={`ml-2 flex items-center text-xs ${
                selectedZoneData.trend === 'down' 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {selectedZoneData.trend === 'down' ? (
                  <><TrendingDown className="h-3 w-3 mr-1" /> {selectedZoneData.percentage}%</>
                ) : (
                  <><TrendingUp className="h-3 w-3 mr-1" /> {selectedZoneData.percentage}%</>
                )}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Showing consumption for all zones on floor {selectedFloor}
        </div>
      )}
      
      <div className="h-40 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={displayData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis 
              dataKey="name" 
              fontSize={10}
              tickFormatter={(value) => value.length > 8 ? `${value.substring(0, 8)}...` : value}
            />
            <YAxis fontSize={10} />
            <Tooltip />
            <Bar 
              dataKey="consumption" 
              fill="#3b82f6" 
              name="Energy (kWh)" 
              radius={[4, 4, 0, 0]} 
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {selectedZoneData && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
          <h3 className="text-sm font-medium mb-2">Today's Hourly Consumption</h3>
          <div className="h-28">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                <XAxis dataKey="hour" fontSize={9} />
                <YAxis fontSize={9} />
                <Tooltip />
                <Bar dataKey="consumption" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default HvacEnergyStats;
